/* eslint-disable max-lines */
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import { visuallyHidden } from '@mui/utils';
import { find, includes, isEmpty, isNil, map, toLower } from 'lodash';
import { differenceInDays, differenceInHours, format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';

import BatchShippingIcon, { batchShippingTooltip } from 'common/components/BatchShippingIcon';
import BusinessAddressIndicator from 'common/components/BusinessAddressIndicator';
import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import { casingFormat, formatEnumLabel, getTrackingInfo, hex2rgba } from 'common/utils';
import { ShipmentStatus } from 'enums/common.enum';
import { deletePrxShipmentApi } from 'api/order';
import BatchShipmentDialog from 'common/modules/BatchShipmentDialog';
import ShippingTimelineModal from 'common/modules/ShippingTimelineModal';
import {
  selectAccessibleFacilities,
  selectCurrentPageItems,
} from '../../../store/selectors/shipmentModuleSelectors';
import { getStateDisplayName } from '../../../store/utils/shipmentUtils';
import {
  fetchShipmentsByPage,
  refreshAfterMutation,
  updateShipmentSortBy,
} from '../../../store/thunks/shipmentModuleThunks';
import { handleError } from '../../../store/thunks/errorHandlerThunks';
import CancelShipmentDialog from '../../WorkQueue/components/CancelShipmentDialog';
import ShipmentViewDialog from '../../WorkQueue/components/ShipmentViewDialog';
import ShipmentNotesTimelineDialog from './ShipmentNotesTimelineDialog';

const getHeadCells = ({ activeCategory, activeSubcategory }) => {
  const showShippedDate = activeCategory === 'shipped' && activeSubcategory !== 'voided';
  const showStatus = activeSubcategory === 'voided';
  const showVoidedDate = activeSubcategory === 'voided';

  return [
    { id: 'expand', label: '' },
    { id: 'referenceId', label: 'Order' },
    { id: 'createdAt', label: 'Age' },
    { id: 'orderDate', label: 'Order Date' },
    { id: 'shipDate', label: 'Ship Date', tooltip: 'Expected Ship Date' },
    ...(showShippedDate
      ? [{ id: 'shippedDate', label: 'Shipped Date', tooltip: 'Label Generated Date' }]
      : []),
    ...(showVoidedDate ? [{ id: 'voidedDate', label: 'Voided At' }] : []),
    { id: 'itemCount', label: '# of Items' },
    ...(showStatus ? [{ id: 'shipmentStatus', label: 'Status' }] : []),
    { id: 'recipientName', label: 'Recipient' },
    { id: 'partnerName', label: 'Partner' },
    { id: 'practiceName', label: 'Practice' },
    { id: 'fulfillingPharmacy', label: 'Facility' },
    { id: 'Shipping', label: 'Shipping Option' },
    { id: 'carrier', label: 'Carrier' },
    ...(activeCategory !== 'awaitingShipment'
      ? [{ id: 'trackingNumber', label: 'Tracking Number' }]
      : []),
    { id: 'tags', label: 'Tags' },
    { id: 'notes', label: 'Notes' },
    { id: 'batchShipment', label: '' },
    { id: 'shippingHistory', label: '' },
    { id: 'actions', label: '' },
  ];
};

const sortableFields = ['createdAt', 'itemCount', 'orderDate'];

const ShipmentStatusLabelMap = {
  [ShipmentStatus.REQUEST_CREATED]: 'Ready To Ship',
  [ShipmentStatus.REQUEST_SHIPPED]: 'Shipped',
};

const ShipmentListTable = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [openBatchDialog, setOpenBatchDialog] = useState(false);
  const [currentBatchShipmentId, setCurrentBatchShipmentId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedShipmentId, setSelectedShipmentId] = useState(null);
  const [openShipmentViewDialog, setOpenShipmentViewDialog] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [shipmentToCancel, setShipmentToCancel] = useState(null);
  const [isCancellingShipment, setIsCancellingShipment] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineShipment, setTimelineShipment] = useState(null);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesDialogNotes, setNotesDialogNotes] = useState([]);
  const [notesDialogReferenceId, setNotesDialogReferenceId] = useState(undefined);

  useEffect(() => {
    const handler = () => setOpenShipmentViewDialog(false);
    window.addEventListener('closeShipmentViewDialog', handler);
    return () => window.removeEventListener('closeShipmentViewDialog', handler);
  }, []);

  const { sortBy, count, loading, currentPage } = useSelector(
    ({ shipmentModule }) => shipmentModule.list
  );
  const { activeCategory, activeSubcategory } = useSelector(({ shipmentModule }) => shipmentModule);
  const headCells = getHeadCells({ activeCategory, activeSubcategory });
  const showShippedDate = activeCategory === 'shipped' && activeSubcategory !== 'voided';
  const showStatus = activeSubcategory === 'voided';
  const showVoidedDate = activeSubcategory === 'voided';
  const columnCount = headCells.length;
  const orders = useSelector(selectCurrentPageItems);
  const facilities = useSelector(selectAccessibleFacilities);

  const totalPages = Math.ceil(count / 25);

  const handleSort = (event) => {
    const sortKey = event.currentTarget?.dataset?.headProperty;

    dispatch(
      updateShipmentSortBy({
        sortBy: {
          sortKey,
          sortOrder: sortBy.sortKey === sortKey && sortBy.sortOrder === 'ASC' ? 'DESC' : 'ASC',
        },
      })
    );
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) return;

    dispatch(fetchShipmentsByPage({ page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleRow = (referenceId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(referenceId)) {
        newSet.delete(referenceId);
      } else {
        newSet.add(referenceId);
      }
      return newSet;
    });
  };

  const handleOpenBatchDialog = (batchShipmentId) => {
    setCurrentBatchShipmentId(batchShipmentId);
    setOpenBatchDialog(true);
  };

  const handleCloseBatchDialog = () => {
    setCurrentBatchShipmentId(null);
    setOpenBatchDialog(false);
  };

  const handleMenuOpen = (event, referenceId) => {
    setAnchorEl(event.currentTarget);
    setSelectedShipmentId(referenceId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedShipmentId(null);
  };

  const handleViewShipment = () => {
    if (selectedShipmentId) {
      const shipment = find(orders, { referenceId: selectedShipmentId });
      if (shipment) {
        setSelectedShipment(shipment);
        setOpenShipmentViewDialog(true);
      }
    }
    handleMenuClose();
  };

  const handleViewShipmentById = (referenceId) => {
    const shipment = find(orders, { referenceId });
    if (shipment) {
      setSelectedShipment(shipment);
      setOpenShipmentViewDialog(true);
    }
  };

  const handleCloseShipmentViewDialog = () => {
    setOpenShipmentViewDialog(false);
    setSelectedShipment(null);
  };

  const handleCancelShipmentClick = () => {
    if (selectedShipmentId) {
      const shipment = find(orders, { referenceId: selectedShipmentId });
      if (shipment) {
        setShipmentToCancel(shipment);
        setCancelDialogOpen(true);
      }
    }
    handleMenuClose();
  };

  const handleConfirmCancelShipment = ({ cancelOrder, reason } = {}) => {
    if (!shipmentToCancel?.shipmentId) return;

    let cancelType;
    if (cancelOrder) {
      cancelType = 'ORDER_CANCEL';
    } else {
      cancelType = 'SHIP_LATER_REVIEW';
    }
    setIsCancellingShipment(true);
    deletePrxShipmentApi({ shipmentId: shipmentToCancel.shipmentId, reason, cancelType })
      .then(() => {
        const message = cancelOrder
          ? 'Shipment and order cancelled successfully'
          : 'Shipment cancelled successfully';
        enqueueSnackbar(message, { variant: 'success' });
        setCancelDialogOpen(false);
        setShipmentToCancel(null);
        setIsCancellingShipment(false);
        dispatch(refreshAfterMutation());
      })
      .catch((error) => {
        enqueueSnackbar('Failed to cancel shipment', { variant: 'error' });
        dispatch(handleError({ error }));
        setIsCancellingShipment(false);
      });
  };

  const getAgeColor = (age) => {
    if (age === null || age === undefined) return theme.palette.text.secondary;
    if (age > 48) return theme.palette.error.main;
    if (age > 24) return theme.palette.warning.main;
    return theme.palette.text.primary;
  };

  // Show loading state when the page data hasn't been fetched yet
  if (loading && isEmpty(orders)) {
    return (
      <Grid item>
        <LoadingModule />
      </Grid>
    );
  }

  return (
    <Grid container direction='column'>
      <BatchShipmentDialog
        batchShipmentId={currentBatchShipmentId}
        onClose={handleCloseBatchDialog}
        open={openBatchDialog}
      />
      <ShipmentNotesTimelineDialog
        open={notesDialogOpen}
        onClose={() => {
          setNotesDialogOpen(false);
          setNotesDialogNotes([]);
          setNotesDialogReferenceId(undefined);
        }}
        notes={notesDialogNotes}
        referenceId={notesDialogReferenceId}
      />
      <ShipmentViewDialog
        key={selectedShipment?.shipmentId}
        open={openShipmentViewDialog}
        onClose={handleCloseShipmentViewDialog}
        shipment={selectedShipment}
        onAfterMutation={() => dispatch(refreshAfterMutation())}
        disableVoidLabel={activeSubcategory === 'voided'}
        hideOrderCancelOption={!!selectedShipment?.isManualShipment}
      />
      <CancelShipmentDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        shipmentId={shipmentToCancel?.referenceId}
        onConfirm={handleConfirmCancelShipment}
        isLoading={isCancellingShipment}
        hideOrderCancelOption={!!shipmentToCancel?.isManualShipment}
      />

      <Grid item>
        <TableContainer>
          <Table sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align='center'
                    sx={{ py: 1.5, px: 1, fontSize: '0.875rem' }}
                  >
                    {!includes(sortableFields, headCell.id) ? (
                      <Tooltip title={headCell.tooltip || ''} arrow placement='top'>
                        <Typography variant='subtitle2'>{headCell.label}</Typography>
                      </Tooltip>
                    ) : (
                      <TableSortLabel
                        active={sortBy.sortKey === headCell.id}
                        direction={
                          sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : 'asc'
                        }
                        onClick={handleSort}
                        data-head-property={headCell.id}
                        sx={{ justifyContent: 'center', width: '100%' }}
                      >
                        <Typography variant='subtitle2'>{headCell.label}</Typography>
                        {sortBy.sortKey === headCell.id ? (
                          <Box component='span' sx={visuallyHidden}>
                            {toLower(sortBy.sortOrder) === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {map(
                orders,
                ({
                  referenceId,
                  shipmentId,
                  shipmentStatus,
                  shipmentCreatedAt,
                  shipmentShippedAt,
                  voidedTimestamp,
                  shipmentItems,
                  recipientName,
                  serviceCode,
                  carrierCode,
                  trackingNumber,
                  isBatchShipment,
                  isBusinessAddress,
                  isManualShipment,
                  itemCount,
                  partnerName,
                  practiceName,
                  fulfillingPharmacyId,
                  shipDate,
                  tags,
                  notes,
                }) => {
                  const orderDate = format(new Date(shipmentCreatedAt), 'MM/dd/yyyy');
                  const ageInHours = differenceInHours(new Date(), new Date(shipmentCreatedAt));
                  const age =
                    ageInHours > 48
                      ? `${differenceInDays(new Date(), new Date(shipmentCreatedAt))}days`
                      : `${ageInHours}h`;
                  const ageColor = getAgeColor(ageInHours);
                  const isExpanded = expandedRows.has(referenceId);

                  const fillingFacility = find(
                    facilities,
                    (facility) => facility?.fulfillingPharmacyId === fulfillingPharmacyId
                  );
                  const isVoidedView = activeSubcategory === 'voided';

                  return (
                    <Fragment key={shipmentId}>
                      <TableRow hover={false} sx={{ backgroundColor: 'inherit' }}>
                        <TableCell sx={{ py: 1.5, width: 50 }}>
                          <IconButton
                            aria-label={`Toggle Details ${referenceId}`}
                            size='small'
                            onClick={() => toggleRow(referenceId)}
                          >
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography
                            variant='body2'
                            onClick={() => handleViewShipmentById(referenceId)}
                            sx={{
                              fontWeight: 500,
                              fontFamily: 'monospace',
                              cursor: 'pointer',
                              color: 'primary.main',
                              textDecoration: 'underline',
                            }}
                          >
                            {referenceId}
                          </Typography>
                        </TableCell>
                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2' sx={{ color: ageColor }}>
                            {age}
                          </Typography>
                        </TableCell>
                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2'>{orderDate}</Typography>
                        </TableCell>
                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2'>
                            {format(new Date(shipDate || shipmentCreatedAt), 'MM/dd/yyyy')}
                          </Typography>
                        </TableCell>
                        {showShippedDate && (
                          <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                            <Typography variant='body2'>
                              {shipmentShippedAt
                                ? format(new Date(shipmentShippedAt), 'MM/dd/yyyy')
                                : '-'}
                            </Typography>
                          </TableCell>
                        )}
                        {showVoidedDate && (
                          <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                            <Typography variant='body2'>
                              {voidedTimestamp
                                ? format(new Date(voidedTimestamp), 'MM/dd/yyyy hh:mm a')
                                : '-'}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2'>{itemCount}</Typography>
                        </TableCell>
                        {showStatus && (
                          <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                            <Typography variant='body2'>
                              {ShipmentStatusLabelMap[shipmentStatus]}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2' sx={{ fontWeight: 500 }}>
                            {casingFormat(recipientName)}
                          </Typography>
                        </TableCell>
                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2'>{casingFormat(partnerName)}</Typography>
                        </TableCell>
                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2'>{casingFormat(practiceName)}</Typography>
                        </TableCell>
                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2'>
                            {fillingFacility?.name && fillingFacility?.state
                              ? `${fillingFacility.name} (${getStateDisplayName(fillingFacility.state)})`
                              : fillingFacility?.name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2'>{formatEnumLabel(serviceCode)}</Typography>
                        </TableCell>
                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2'>{formatEnumLabel(carrierCode)}</Typography>
                        </TableCell>
                        {activeCategory !== 'awaitingShipment' && (
                          <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                            {!trackingNumber && <Typography variant='body2'> - </Typography>}
                            {trackingNumber && isVoidedView && (
                              <Typography
                                variant='body2'
                                sx={{
                                  fontSize: '1em',
                                  textDecoration: 'line-through',
                                  color: theme.palette.text.disabled,
                                }}
                              >
                                {trackingNumber}
                              </Typography>
                            )}
                            {trackingNumber && !isVoidedView && (
                              <Link
                                href={getTrackingInfo({ trackingNumber }).trackingUrl}
                                target='_blank'
                                sx={{ fontSize: '1em' }}
                              >
                                {trackingNumber}
                              </Link>
                            )}
                          </TableCell>
                        )}
                        <TableCell align='center' sx={{ py: 1.5, px: 1, minWidth: 100 }}>
                          {(!tags || tags.length === 0) && (
                            <Typography variant='body2' color='text.disabled'>
                              N/A
                            </Typography>
                          )}
                          {tags && tags.length > 0 && (
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr auto 1fr',
                                alignItems: 'center',
                                columnGap: 0.5,
                              }}
                            >
                              <Box />
                              <Tooltip title={tags[0].tooltip || ''} arrow placement='top'>
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    bgcolor: tags[0].color
                                      ? hex2rgba(tags[0].color, 0.18)
                                      : 'grey.100',
                                    border: '1px solid',
                                    borderColor: tags[0].color
                                      ? hex2rgba(tags[0].color, 0.45)
                                      : 'grey.400',
                                    borderRadius: '999px',
                                    px: 1,
                                    py: 0.3,
                                    cursor: 'default',
                                    justifySelf: 'center',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 7,
                                      height: 7,
                                      borderRadius: '50%',
                                      bgcolor: tags[0].color || 'grey.500',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography
                                    variant='caption'
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '0.72rem',
                                      lineHeight: 1.2,
                                      whiteSpace: 'nowrap',
                                      color: tags[0].color || 'text.primary',
                                    }}
                                  >
                                    {tags[0].label}
                                  </Typography>
                                </Box>
                              </Tooltip>
                              {tags.length > 1 ? (
                                <Tooltip
                                  arrow
                                  placement='top'
                                  title={
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.25,
                                        py: 0.5,
                                      }}
                                    >
                                      {tags.slice(1).map((tag) => (
                                        <Typography
                                          key={tag.ruleId}
                                          variant='caption'
                                          sx={{ fontWeight: 500 }}
                                        >
                                          {tag.label}
                                          {tag.tooltip ? ` — ${tag.tooltip}` : ''}
                                        </Typography>
                                      ))}
                                    </Box>
                                  }
                                >
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      bgcolor: 'grey.50',
                                      border: '1px solid',
                                      borderColor: 'grey.400',
                                      borderRadius: '999px',
                                      px: 0.875,
                                      py: 0.3,
                                      cursor: 'default',
                                      minWidth: 26,
                                      justifySelf: 'start',
                                    }}
                                  >
                                    <Typography
                                      variant='caption'
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '0.72rem',
                                        color: 'text.secondary',
                                      }}
                                    >
                                      {`+${tags.length - 1}`}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Box />
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          {notes && notes.length > 0 ? (
                            <Tooltip
                              title={`View ${notes.length} note${notes.length > 1 ? 's' : ''}`}
                              arrow
                              placement='top'
                            >
                              <IconButton
                                size='small'
                                aria-label={`View notes for ${referenceId}`}
                                onClick={() => {
                                  setNotesDialogNotes(notes);
                                  setNotesDialogReferenceId(referenceId);
                                  setNotesDialogOpen(true);
                                }}
                                sx={{
                                  color: theme.palette.warning.warningColour ?? '#FFC107',
                                }}
                              >
                                <StickyNote2Icon fontSize='small' />
                                <Typography variant='caption' sx={{ ml: 0.5, fontWeight: 600 }}>
                                  {notes.length}
                                </Typography>
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant='body2' color='text.disabled'>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          {isBatchShipment && referenceId && (
                            <Tooltip
                              title={batchShippingTooltip({
                                isBusinessAddress,
                                isShipped: true,
                                isClickable: true,
                              })}
                              arrow
                              placement='top'
                            >
                              <BatchShippingIcon
                                onClick={() => handleOpenBatchDialog(referenceId)}
                                color={perfectRxTheme.palette.success.main}
                                showBadge={isBusinessAddress}
                                sx={{
                                  '&:hover': {
                                    transform: 'scale(1.1)',
                                    transition: 'transform 0.2s',
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                          {!isBatchShipment && isBusinessAddress && <BusinessAddressIndicator />}
                          {isManualShipment && (
                            <Tooltip title='Manually created shipment' arrow placement='top'>
                              <Chip
                                label='Manual'
                                size='small'
                                sx={{
                                  bgcolor: '#E3F2FD',
                                  color: '#1565C0',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  height: 20,
                                  cursor: 'default',
                                }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align='center' sx={{ py: 1.5, px: 0 }}>
                          <Tooltip title='View Shipping History' arrow placement='top'>
                            <IconButton
                              size='small'
                              onClick={() => {
                                const shipment = find(orders, { referenceId });
                                if (shipment) setTimelineShipment(shipment);
                                setTimelineOpen(true);
                              }}
                              aria-label='View shipping history'
                              sx={{ color: 'primary.main' }}
                            >
                              <HistoryIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <IconButton
                            size='small'
                            onClick={(e) => handleMenuOpen(e, referenceId)}
                            aria-label='Shipment actions'
                          >
                            <MoreVertIcon fontSize='small' />
                          </IconButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl) && selectedShipmentId === referenceId}
                            onClose={handleMenuClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          >
                            <MenuItem onClick={handleViewShipment}>View</MenuItem>
                            {shipmentStatus === ShipmentStatus.REQUEST_CREATED && (
                              <MenuItem
                                onClick={handleCancelShipmentClick}
                                sx={{ color: 'error.main' }}
                              >
                                Cancel Shipment
                              </MenuItem>
                            )}
                          </Menu>
                        </TableCell>
                      </TableRow>

                      {/* Expanded row: shipment items detail */}
                      <TableRow>
                        <TableCell
                          colSpan={columnCount}
                          sx={{ p: 0, border: 'none', backgroundColor: 'inherit' }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Collapse in={isExpanded} timeout='auto' sx={{ width: '100%' }}>
                              {shipmentItems && shipmentItems.length > 0 && (
                                <Box
                                  sx={{
                                    py: 2,
                                    px: 3,
                                    width: '100%',
                                    backgroundColor: theme.palette.grey[50],
                                  }}
                                >
                                  <Table size='small'>
                                    <TableHead>
                                      <TableRow>
                                        {['Drug Name', 'Order Id', 'SKU', 'Quantity'].map(
                                          (header) => (
                                            <TableCell
                                              key={header}
                                              align='center'
                                              sx={{
                                                fontWeight: 600,
                                                backgroundColor: theme.palette.grey[100],
                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                                py: 1,
                                                px: 1,
                                              }}
                                            >
                                              {header}
                                            </TableCell>
                                          )
                                        )}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {map(shipmentItems, (item) => (
                                        <TableRow
                                          key={item.lineItemId}
                                          hover={false}
                                          sx={{ backgroundColor: 'inherit' }}
                                        >
                                          <TableCell align='center' sx={{ py: 1, px: 1 }}>
                                            <Typography variant='body2'>{item.name}</Typography>
                                          </TableCell>
                                          <TableCell align='center' sx={{ py: 1, px: 1 }}>
                                            <Typography
                                              variant='body2'
                                              sx={{ fontFamily: 'monospace' }}
                                            >
                                              {item.orderId}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align='center' sx={{ py: 1, px: 1 }}>
                                            <Typography
                                              variant='body2'
                                              sx={{ fontFamily: 'monospace' }}
                                            >
                                              {item.sku || '-'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align='center' sx={{ py: 1, px: 1 }}>
                                            <Typography variant='body2'>{item.quantity}</Typography>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              )}
                            </Collapse>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                }
              )}
            </TableBody>

            {!isNil(orders) && !isEmpty(orders) && totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={columnCount} align='right' sx={{ borderBottom: 'none' }}>
                    <Pagination
                      sx={{
                        justifyContent: 'flex-end',
                        alignItems: 'flex-end',
                        display: 'flex',
                      }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {!isNil(orders) && isEmpty(orders) && !loading && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}

      <ShippingTimelineModal
        open={timelineOpen}
        onClose={() => {
          setTimelineOpen(false);
          setTimelineShipment(null);
        }}
        shipmentId={timelineShipment?.shipmentId}
        referenceId={timelineShipment?.referenceId}
      />
    </Grid>
  );
};

export default ShipmentListTable;
